import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AuthService, NotifyService } from '@app/services';
import { CompanySetting } from '@models/settings/company-setting.model';

@Component({
    templateUrl: './onboarding-banking-info.template.html',
    styleUrls: ['./onboarding-banking-info.style.scss'],
})
export class OnboardingBankingInfoView implements OnInit {
    isLoading = false;
    settings: {
        bankingInformationRequired: CompanySetting;
        bankingInformationOnboarding: CompanySetting;
        sendHiringQuestionnaireToNewHires: CompanySetting; // TODO deprecated
        sendWelcomeMessageToNewHires: CompanySetting; // TODO deprecated
    };

    constructor(
        private location: Location,
        private notify: NotifyService,
        private auth: AuthService
    ) {}

    async ngOnInit(): Promise<void> {
        const [companySettings] = await CompanySetting.param('company', this.auth.company.id)
            .where('module', 'EmployeeManagement')
            .with('setting')
            .get();
        this.registerCompanySettings(companySettings);
        this.isLoading = false;
    }

    async onBankingInfoRadioChange(event: boolean): Promise<void> {
        this.combinedSettings(event);
    }

    async onSave(): Promise<void> {
        try {
            const settingsToSave: Promise<void>[] = [];
            for (const key in this.settings) {
                const setting = this.settings[key];
                settingsToSave.push(setting.param('company', this.auth.company.id).save());
            }
            await Promise.all(settingsToSave);
            this.location.back();
        } catch (err) {
            this.notify.error(err.message || 'employees.onboardingSettings.couldNotSaveSetting');
        }
    }

    onCancel(): void {
        this.location.back();
    }

    private registerCompanySettings(companySettings: CompanySetting[]): void {
        const bankingInformationRequired = companySettings.find(
            (setting) => setting.setting.name === 'bankingInformation.required'
        );
        const bankingInformationOnboarding = companySettings.find(
            (setting) => setting.setting.name === 'bankingInformation.collectDuringOnboarding'
        );
        const sendHiringQuestionnaireToNewHires = companySettings.find(
            (setting) => setting.setting.name === 'sendHiringQuestionnaireToNewHiresByDefault'
        );
        const sendWelcomeMessageToNewHires = companySettings.find(
            (setting) => setting.setting.name === 'sendWelcomeMessageToNewHiresByDefault'
        );

        this.settings = {
            bankingInformationRequired,
            bankingInformationOnboarding,
            sendHiringQuestionnaireToNewHires,
            sendWelcomeMessageToNewHires,
        };

        // Show error if registering fails
        if (
            !(
                this.settings.bankingInformationRequired &&
                this.settings.bankingInformationOnboarding &&
                this.settings.sendHiringQuestionnaireToNewHires &&
                this.settings.sendWelcomeMessageToNewHires
            )
        ) {
            this.notify.error('employees.onboardingSettings.couldNotRegisterCompanySettings');
            return;
        }

        this.combinedSettings(bankingInformationRequired.value);
    }

    /**
     * Currently, we have 4 settings that have caused a lot of user confusion.
     *
     *  1. Banking information required for all employees
     *  2. Collect new employee's banking information during onboarding
     *  3. Send hiring questionnaire to new hires by default
     *  4. Send welcome message to new hires by default
     *
     *  We’re modifying these to a single setting: Bank information is required for all employees
     *
     *  What happens to the other settings?
     *
     *  We’re combining the first two into this one setting. If banking information is required, it needs to be
     *  collected during onboarding. If a customer wants to enter that information themselves, they can disable this
     *  setting and enter it manually for each employee.
     *   - To require banking information both have to be on.
     *   - No actual split between new hires and existing employees.
     *
     * During the onboarding flow, we ask if you want to send your new hire questions, making this redundant.
     *  - If you have them, they’ll be sent by default, and you can change them in the onboarding steps.
     *
     * Finally, the welcome message behaves similarly, in that it can be included, edited, or removed in the onboarding flow.
     *  - This toggle doesn’t behave as it should, and it’s easier to just remove it.
     *
     *  Turns out the code for company settings is hella complicated.
     *  Now, the confusion is passed on the developer!
     *  Could this _really_ be changed to a single setting in the future? Maybe! Good luck.
     */
    private combinedSettings(val: boolean): void {
        this.settings.bankingInformationRequired.value = val;
        this.settings.bankingInformationOnboarding.value = val;
        this.settings.sendHiringQuestionnaireToNewHires.value = true;
        this.settings.sendWelcomeMessageToNewHires.value = true;
    }
}
